/**
 * Registry of editable site copy blocks.
 * Each entry defines the page, key, a human label for the admin UI,
 * and the hardcoded fallback shown when no DB override exists.
 */
export type SiteBlockDef = {
  page_slug: string;
  block_key: string;
  label: string;
  fallback: string;
  multiline?: boolean;
};

export const SITE_BLOCKS: SiteBlockDef[] = [
  // Home — Hero
  {
    page_slug: "home",
    block_key: "hero_eyebrow_left",
    label: "Hero eyebrow (left)",
    fallback: "Alabama",
  },
  {
    page_slug: "home",
    block_key: "hero_eyebrow_right",
    label: "Hero eyebrow (right)",
    fallback: "Civic Infrastructure",
  },
  {
    page_slug: "home",
    block_key: "hero_headline",
    label: "Hero headline",
    fallback: "Powering the work that moves communities forward.",
    multiline: true,
  },
  {
    page_slug: "home",
    block_key: "hero_subheadline",
    label: "Hero subheadline",
    fallback:
      "We're a Strategic Communications firm for small organizations and businesses. We support the goals and ideas that drive our communities in Alabama forward.",
    multiline: true,
  },

  // Home — Mission focus card
  {
    page_slug: "home",
    block_key: "mission_eyebrow",
    label: "Mission card eyebrow",
    fallback: "Welcome",
  },
  {
    page_slug: "home",
    block_key: "mission_headline",
    label: "Mission card headline",
    fallback: "One place to work on Alabama's ideas.",
    multiline: true,
  },
  {
    page_slug: "home",
    block_key: "mission_body",
    label: "Mission card body",
    fallback:
      "The Emerging Market is an Alabama public benefit corporation (LLC) and government affairs firm. We build strategies for grassroots organizations and businesses that rarely have a partner in the room when decisions get made. We do lobbying, strategic communication, advocacy, research, and economic development — all connected to a live view of bills, grants, partners, and insights you can explore below.",
    multiline: true,
  },

  // About Us
  {
    page_slug: "about",
    block_key: "intro_paragraph_1",
    label: "Intro paragraph (top)",
    fallback:
      "The Emerging Market is an Alabama public benefit corporation (LLC) and government affairs firm. We focus on lobbying, strategic communication, advocacy, research, and economic development. We were built for grassroots organizations and businesses that rarely have a partner in the room when decisions get made.",
    multiline: true,
  },
  {
    page_slug: "about",
    block_key: "intro_paragraph_2",
    label: "Intro paragraph (second)",
    fallback:
      "We combine classic government affairs work — bill tracking, meeting legislators, building coalitions, staying on message — with the live data tools on this site. Our clients, from community groups to small businesses, use the same tools we do.",
    multiline: true,
  },

  // Insights
  {
    page_slug: "insights",
    block_key: "page_intro",
    label: "Page intro",
    fallback:
      "Follow the policy news, funding trends, and practical guides that help your group do more.",
    multiline: true,
  },

  // Events
  {
    page_slug: "events",
    block_key: "page_intro",
    label: "Page intro",
    fallback:
      "Run campaigns, share partner events, and find training that builds skills across Alabama's advocacy, policy, business, and community groups.",
    multiline: true,
  },

  // Opportunities
  {
    page_slug: "opportunities",
    block_key: "hero_headline",
    label: "Hero headline",
    fallback: "Capital that moves Alabama forward",
    multiline: true,
  },
  {
    page_slug: "opportunities",
    block_key: "page_intro",
    label: "Page intro",
    fallback:
      "A live feed of grants and funding programs matched to our five focus areas. Save what fits, mark what you have applied to, and track everything from your profile.",
    multiline: true,
  },
];

export type SiteBlockMap = Record<string, string>;

/**
 * Returns the override value for a block if present, otherwise the
 * hardcoded fallback. Always safe to call even if blocks is empty.
 */
export function getBlock(blocks: SiteBlockMap | undefined, key: string, fallback: string): string {
  const v = blocks?.[key];
  if (typeof v === "string" && v.trim().length > 0) return v;
  return fallback;
}

/** Build a key->value map keyed by block_key for a given page. */
export function indexBlocks(rows: Array<{ page_slug: string; block_key: string; value: string }>, page_slug: string): SiteBlockMap {
  const out: SiteBlockMap = {};
  for (const r of rows) {
    if (r.page_slug === page_slug) out[r.block_key] = r.value;
  }
  return out;
}